import React, { useEffect, useMemo, useRef } from "react";
import {
  Button,
  FormFieldSet,
  PageHeader,
  PageTitle,
  showToast,
  useFormHandler,
} from "../../../../components";
import {
  cityStateListOptions,
  countryListOptions,
} from "../../../../assets/data/projectData";
import {
  ContactStatus,
  ContactType,
  Gender,
} from "../../../../assets/data/enumData";
import { useDispatch, useSelector } from "react-redux";
import { useLoginUser } from "../../../../hooks/useLoginUser";
import { addContactStart } from "../../../../redux/slices/client/clientSlice";
import {
  addToDraft,
  getVendorOneStart,
  updateContactStart,
} from "../../../../redux/slices/vendor/vendorSlice";
import useDropdownData from "../../../../hooks/useDropdownData";
import { debounce } from "../../../../utils";

const Contacts = ({ setActiveTab, isEditable, editVendor = "", vendorId }) => {
  const dispatch = useDispatch();
  const containerRef = useRef(null);
  const vendorProfileUiid = useSelector(
    (state) => state.vendor.vendorProfileUiid
  );
  const vendorDetails = useSelector(
    (state) => state.vendor.currentVendor.vendorDetails
  );
  const { userData: loginUserData } = useLoginUser();
  const loginUserUiid = loginUserData?.uiid;
  const { formData, errors, handleChange, validateAllFields } = useFormHandler(
    {
      representativeName: "",
      jobTitle: "",
      contactType: "",
      gender: "",
      emailAddress: "",
      mobilePhoneCountryCode: "",
      mobilePhone: "",
      homePhoneCountryCode: "",
      homePhone: "",
      addressLine1: "",
      addressLine2: "",
      country: "",
      state: "",
      city: "",
      zipCode: "",
      dateOfBirth: "",
    },
    {
      representativeName: { type: "text", required: true },
      // jobTitle: { type: "text", required: true },
      mobilePhone: {
        type: "phone",
        minLength: 9,
        maxLength: 15,
        required: true,
      },
      homePhone: { type: "phone", minLength: 9, maxLength: 15 },
    }
  );
  useEffect(() => {
    if (vendorProfileUiid) {
      dispatch(
        getVendorOneStart({
          vendorUiid: vendorProfileUiid,
          retrieve: `contacts`,
          ref: `Contact`,
        })
      );
    }
  }, [vendorId]);

  const handleBack = () => {
    setTimeout(() => {
      // Move to back tab
      setActiveTab((prev) => prev - 1);
    }, 500);
  };
  useEffect(() => {
    if (
      (vendorProfileUiid && vendorDetails?.contacts?.length > 0) ||
      Object.keys(vendorDetails).length > 0
    ) {
      // if (
      //   (vendorProfileUiid && vendorDetails?.contacts?.length > 0) ||
      //   Object.keys(vendorDetails).length > 0
      // ) {
      const contact = vendorDetails?.contacts?.[0]
        ? vendorDetails.contacts[0]
        : {};

      handleChange("uiid", contact.uiid || "");
      handleChange(
        "representativeName",
        contact.representativeName
          ? contact.representativeName
          : "" || ""
      );
      handleChange("jobTitle", contact.jobTitle || "");
      handleChange("contactType", contact.contactType || "");
      handleChange("gender", contact.gender || "");
      handleChange("emailAddress", contact.email || "");
      handleChange("mobilePhoneCountryCode", contact.phone?.main?.code || "");
      handleChange("mobilePhone", contact.phone?.main?.number || "");
      handleChange("workPhoneCountryCode", contact.phone?.work?.code || "");
      handleChange("workPhone", contact.phone?.work?.number || "");
      handleChange("homePhoneCountryCode", contact.phone?.home?.code || "");
      handleChange("homePhone", contact.phone?.home?.number || "");
      handleChange("dateOfBirth", contact.dob || "");
      handleChange("addressLine1", contact.address?.address1 || "");
      handleChange("addressLine2", contact.address?.address2 || "");
      handleChange("country", contact.address?.country || "");
      handleChange("state", contact.address?.state || "");
      handleChange("city", contact.address?.city || "");
      handleChange("zipCode", contact.address?.zipCode || "");
    }
    // }, [vendorDetails]);
  }, [vendorProfileUiid, Object.keys(vendorDetails).length]);

  const { data: countryOptions, handleScroll: handleCountriesScroll } =
    useDropdownData("countriesUsAndCanada");

  const countryId = countryOptions.find(
    (item) => item.label === formData.country
  )?.id;

  const {
    data: stateOptions,
    searchValue: stateSearch,
    handleSearch: handleStateSearch,
    resetSearch: resetStateSearch,
    handleScroll: handleStateScroll,
  } = useDropdownData(
    "states",
    10,
    { country: formData.country },
    { countrySearch: countryId }
  );

  const {
    data: cityOptions,
    searchValue: citySearch,
    handleSearch: handleCitySearch,
    resetSearch: resetCitySearch,
    handleScroll: handleCityScroll,
  } = useDropdownData(
    "cities",
    10,
    { country: formData.country, state: formData.state },
    { countrySearch: countryId, stateSearch: formData.state }
  );
  const handleSave = () => {
    if (
      Object.values(formData).every(
        (value) =>
          !value ||
          (typeof value === "object" && Object.values(value).every((v) => !v))
      )
    ) {
      setActiveTab((prev) => prev + 1);
      return;
    }
    const isValid = validateAllFields();
    if (isValid) {
      try {
        const contact = formData;

        const contactPayload = {
          phone: {
            main: {
              code: contact.mobilePhoneCountryCode,
              number: contact.mobilePhone,
            },
            work: {
              code: contact.workPhoneCountryCode,
              number: contact.workPhone,
            },
            home: {
              code: contact.homePhoneCountryCode,
              number: contact.homePhone,
            },
          },
          representativeName: contact.representativeName,
          jobTitle: contact.jobTitle?.value,
          contactType: contact.contactType?.value,
          gender: contact.gender,
          email: contact.emailAddress,
          dob: contact.dateOfBirth,
          address: {
            address1: contact.addressLine1,
            address2: contact.addressLine2,
            country: contact.country,
            state: contact.state,
            city: contact.city,
            zipCode: contact.zipCode,
            comments: contact.addressLineTwo,
          },
        };
        if (isEditable && contact.uiid) {
          dispatch(
            updateContactStart({
              coll: "Contact",
              uiid: contact.uiid,
              payload: [
                {
                  type: "SET",
                  value: contactPayload,
                },
              ],
            })
          );
          setActiveTab((prev) => prev + 1);
        } else {
          dispatch(
            addContactStart({
              submissionData: [
                {
                  ...contactPayload,
                  addedBy: loginUserUiid,
                  entityRefId: vendorProfileUiid,
                  entityType: "Vendor",
                },
              ],
              onSuccess: () => {
                dispatch(
                  getVendorOneStart({
                    vendorUiid: vendorProfileUiid,
                    retrieve: `contacts`,
                    ref: `Contact`,
                  })
                );
                setActiveTab((prev) => prev + 1);
              },
            })
          );
        }
      } catch (error) {
        showToast("Error occured while saving vendor contacts", "error", 5000);
        console.error("Error saving vendor contacts:", error);
      }
    } else {
      showToast("Please fill all the required fields before saving", "error", 5000);
      console.log("Please fix the errors before submitting.");
    }
  };

  const handleDraft = useMemo(
    () =>
      debounce((value) => {
        dispatch(
          // addToDraft({
          //   [field]: value ? value : "",
          // })
          addToDraft({ ...value })
        );
      }, 1000),
    [dispatch]
  );
  useEffect(() => {
    let contact = formData || {};
    let vendorData = {
      contacts: [
        {
          uiid: contact?.uiid,
          representativeName: contact?.representativeName,
          jobTitle: contact?.jobTitle?.value,
          contactType: contact?.contactType?.value,
          gender: contact?.gender,
          email: contact?.emailAddress,
          phone: {
            main: {
              code: contact?.mobilePhoneCountryCode,
              number: contact?.mobilePhone,
            },
            work: {
              code: contact?.workPhoneCountryCode,
              number: contact?.workPhone,
            },
            home: {
              code: contact?.homePhoneCountryCode,
              number: contact?.homePhone,
            },
          },
          dob: contact?.dateOfBirth,
          address: {
            address1: contact?.addressLine1,
            address2: contact?.addressLine2,
            country: contact?.country,
            state: contact?.state,
            city: contact?.city,
            zipCode: contact?.zipCode,
            comments: contact?.addressLineTwo,
          },
        },
      ],
    };
    // if (vendorProfileUiid || Object.keys(vendorDetails).length > 0) {
    handleDraft(vendorData);
    // }
  }, [formData]);
  return (
    <div className="flex flex-col w-full h-full px-8 py-3 overflow-auto rounded">
      <div className="mb-8">
        <PageHeader
          Section={
            <div className="flex">
              <PageTitle title={"Contacts"} icon={"badge"} tag="h5" />
            </div>
          }
          RightSection={
            <>
              {isEditable && editVendor && (
                <>
                  <Button
                    title="Back"
                    size="sm"
                    icon={"arrow_left_alt"}
                    onClick={handleBack}
                  />
                  <Button title="Save" size="sm" onClick={handleSave} />
                </>
              )}
            </>
          }
        />
      </div>
      <div className="grid xs:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-6 max-w-7xl">
        <div>
          <FormFieldSet
            type="text"
            title={"Representative Name"}
            placeholder="Enter Name"
            value={formData.representativeName}
            onChange={(value) => handleChange("representativeName", value)}
            error={errors.representativeName}
            disabled={!isEditable}
          />
        </div>
        <div>
          <FormFieldSet
            type="jobTitle"
            title={"Job Title"}
            placeholder="Enter Job Title"
            value={formData.jobTitle}
            onChange={(value) => handleChange("jobTitle", value)}
            error={errors.jobTitle}
            disabled={!isEditable}
            showButton
            showSearch
          />
        </div>
        <div>
          <FormFieldSet
            type="contactType"
            title={"Contact Type"}
            placeholder="Select Contact Type"
            value={formData.contactType}
            onChange={(value) => handleChange("contactType", value)}
            error={errors.contactType}
            options={ContactType}
            showButton
            showSearch
            disabled={!isEditable}
          />
        </div>
        <div>
          <FormFieldSet
            type="select"
            title={"Gender"}
            placeholder="Select Gender"
            value={formData.gender || ""}
            onChange={(value) => handleChange("gender", value)}
            error={errors.gender}
            options={Gender}
            disabled={!isEditable}
          />
        </div>
        <div>
          <FormFieldSet
            type="text"
            title={"Email Address"}
            placeholder="Enter Email Address"
            value={formData.emailAddress}
            onChange={(value) => handleChange("emailAddress", value)}
            error={errors.emailAddress}
            disabled={!isEditable}
          />
        </div>
        <div>
          <FormFieldSet
            type="phone"
            title={"Enter Mobile"}
            placeholder="Enter Mobile Number"
            value={formData.mobilePhone}
            onChange={(fullValue, details) => {
              handleChange("mobilePhone", details.number);
              handleChange("mobilePhoneCountryCode", details.countryCode);
            }}
            error={errors.mobilePhone}
            disabled={!isEditable}
          />
        </div>
        <div>
          <FormFieldSet
            type="phone"
            title={"Home Phone"}
            placeholder="Enter Home Phone"
            value={formData.homePhone}
            onChange={(fullValue, details) => {
              handleChange("homePhone", details.number);
              handleChange("homePhoneCountryCode", details.countryCode);
            }}
            error={errors.homePhone}
            disabled={!isEditable}
          />
        </div>
        <div>
          <FormFieldSet
            type="text"
            title={"Address Line 1"}
            placeholder="Enter Address"
            value={formData.addressLine1}
            onChange={(value) => handleChange("addressLine1", value)}
            error={errors.addressLine1}
            disabled={!isEditable}
          />
        </div>
        <div>
          <FormFieldSet
            type="text"
            title={"Address Line 2"}
            placeholder="Enter Address"
            value={formData.addressLine2}
            onChange={(value) => handleChange("addressLine2", value)}
            error={errors.addressLine2}
            disabled={!isEditable}
          />
        </div>
        <div>
          <FormFieldSet
            type="select"
            title="Country"
            keyname="countries"
            placeholder="Select Country"
            options={countryOptions}
            value={formData.country}
            onChange={(value) => handleChange("country", value)}
            error={errors.country}
            disabled={!isEditable}
          />
        </div>
        <div>
          <FormFieldSet
            type="select"
            title="State"
            keyname="states"
            placeholder="Select State"
            options={stateOptions}
            searchValue={stateSearch}
            onSearch={handleStateSearch}
            onClose={resetStateSearch}
            showSearch
            value={formData.state}
            onChange={(value) => handleChange("state", value)}
            error={errors.state}
            disabled={!isEditable || !formData.country}
            refData={containerRef}
            handleScroll={() => handleStateScroll(containerRef)}
          />
        </div>
        <div>
          <FormFieldSet
            type="select"
            title="City"
            keyname="cities"
            placeholder="Select City"
            options={cityOptions}
            searchValue={citySearch}
            onSearch={handleCitySearch}
            onClose={resetCitySearch}
            showSearch
            value={formData.city}
            onChange={(value) => handleChange("city", value)}
            error={errors.city}
            disabled={!isEditable || !formData.country || !formData.state}
            refData={containerRef}
            handleScroll={() => handleCityScroll(containerRef)}
          />
        </div>
        <div>
          <FormFieldSet
            type="text"
            title={"Zip Code"}
            placeholder="Enter Zipcode"
            value={formData.zipCode}
            onChange={(value) => handleChange("zipCode", value)}
            error={errors.zipCode}
            disabled={!isEditable}
          />
        </div>
        <div>
          <FormFieldSet
            type="datepicker"
            title={"Date of Birth"}
            placeholder="Select Date of Birth"
            value={formData.dateOfBirth}
            onChange={(value) => handleChange("dateOfBirth", value)}
            error={errors.dateOfBirth}
            maxDate={new Date()}
            disabled={!isEditable}
            showDropdown={false}
          />
        </div>
      </div>
    </div>
  );
};

export default Contacts;
